/**
 * What a tap on one of the Next Steps pushes does (#1692, api#1123).
 *
 * WHY ANYTHING AT ALL. Both pushes land on `/tabs/home`, and `App.vue` mounts
 * `PendingDeviceRequestHost` and `ResetPendingNoticeHost`, which present on any
 * route — so navigation is already handled and neither host needs telling where
 * to appear. What they DO need is a status that is not the launch snapshot.
 *
 * A cold start from a tap reads the status once, at boot, and both hosts decide
 * from it. On a warm resume there may be no read at all between the push
 * arriving and the member tapping it. Either way the member has just been told
 * something happened, and the app would show them the state from before it did:
 * "no pending devices" on the very screen they opened to approve one. The poll
 * would catch up within its interval, but only where a poll is running, and the
 * seconds in between are exactly the ones the member is looking at.
 *
 * ONLY OUR TAPS. `kind` comes from the payload the API sets (api#1139). A
 * giving receipt or an announcement must not drag the encryption status into a
 * read the member did not ask for, and the timing below cannot attribute a tap
 * it cannot name. A push that predates `kind` carries none, and is ignored here
 * for the same reason — the in-app paths remain the floor, which is what the
 * issue requires of a member with notifications off.
 */
import { onNotificationTap } from '@/utils/notification';
import { trackEvent } from '@/utils/mixpanel';

/**
 * The `kind` values api#1139 sets. Named here so a mismatch is one grep, not a
 * feature that silently does nothing.
 */
export const PUSH_KINDS = Object.freeze({
  PENDING_DEVICE: 'next-steps-device-request',
  RESET_HOLD: 'next-steps-reset-hold',
});

const OURS = new Set(Object.values(PUSH_KINDS));

/**
 * @param {{ store: object, track?: typeof trackEvent }} args
 * @returns {() => void} stops listening
 */
export function installPushTapRefresh({ store, track = trackEvent }) {
  if (!store) throw new TypeError('installPushTapRefresh needs the root Vuex store');

  return onNotificationTap(({ kind }) => {
    if (!OURS.has(kind)) return;

    // No content and no identifiers: which of the two pushes was tapped, and
    // nothing else. Decision 10 keeps telemetry on with note content masked;
    // this carries none to mask. The durations decision 16 wants are recorded
    // where the member acts, not here — a tap is not an approval.
    track('next_steps_push_tapped', { kind });

    // Never awaited and never allowed to reject: this runs inside a platform
    // callback at boot, where an unhandled rejection is one in the app shell.
    // A failed read is recorded in `state.readError` by `refreshStatus`
    // itself, and the poll and the ordinary launch path both try again.
    store.dispatch('courses-devices/refreshStatus').catch(error => {
      console.warn('[CoursesE2EE] A push tap could not refresh the status:', error);
    });
  });
}
